/**
 * Reading a response's body once its headers are in: the caller's abort that ends the read, a
 * body that breaks off, a success whose body is not JSON, and the bounds a refusal's body is
 * read under.
 *
 * A body is read through a reader this module opens, so the read can be cancelled: on the
 * caller's abort, and at a refusal's bounds. Cancelling lets go of the body's lock and of its
 * connection on every runtime, where the runtime's own `text()` holds both until the body ends.
 *
 * Apart from the transport because a Data API call, a collaboration signal and an event-stream
 * open read their bodies by these rules too, and `@mountaya/client/events` imports the transport
 * as a type alone: kept there, these rules would carry the transport class into that entry.
 */

import { parseJsonWithDates } from "./dates.js";
import {
  errorFromBody,
  errorFromResponse,
  type FailureDeclaration,
  type MountayaApiError,
  MountayaConnectionError,
} from "./errors.js";
import { abortReasonOf } from "./retry.js";
import type { RequestArgs } from "./transport.js";

/**
 * REFUSAL_WAIT_MS bounds how long a refused call waits for the refusal's body, in milliseconds.
 * The API's error envelope is small and follows the status at once, so a body still arriving
 * after this long comes from something on the network path, and waiting on it would hold the
 * failure back from a caller that acts on one.
 */
const REFUSAL_WAIT_MS = 5_000;

/**
 * MAX_REFUSAL_BYTES bounds how much of a refusal's body is read, in bytes. The API's error
 * envelope is a few entries long, so a body past this is not one, and read whole it would grow
 * the reader for as long as whatever sent it kept sending.
 */
const MAX_REFUSAL_BYTES = 64 * 1024;

/**
 * isAborted reads whether a caller's signal has aborted, afresh at every call: a signal aborts
 * between two reads, which a check the compiler narrows once would not see.
 */
export const isAborted = (signal: AbortSignal): boolean => signal.aborted;

/**
 * settleOnAbort settles as `work` does, unless the caller's `signal` aborts first: then it rejects
 * at once with {@link abortReasonOf} the signal, and so it does when the signal has aborted by the
 * time `work` settles. With no signal it is `work` itself.
 *
 * It is what ends a call on its caller's abort where the work cannot be cancelled: a body the
 * runtime buffered whole and exposes no stream of (React Native), read through the runtime's own
 * `text()`. There the work is abandoned rather than stopped. A body that streams is read through
 * {@link readText}, {@link readBytes} or {@link readFailure}, which cancel it.
 */
export const settleOnAbort = async <T>(
  work: () => Promise<T>,
  signal: AbortSignal | undefined,
): Promise<T> => {
  if (signal === undefined) {
    return work();
  }

  if (isAborted(signal)) {
    throw abortReasonOf(signal);
  }

  let stopWatching = () => {};
  const aborted = new Promise<never>((_resolve, reject) => {
    const onAbort = () => {
      reject(abortReasonOf(signal));
    };
    signal.addEventListener("abort", onAbort, { once: true });
    stopWatching = () => {
      signal.removeEventListener("abort", onAbort);
    };
  });

  try {
    const settled = await Promise.race([work(), aborted]);

    // The abort may have reached the work first and been swallowed there, as a failure's body
    // read is: the call still ends on the abort.
    if (isAborted(signal)) {
      throw abortReasonOf(signal);
    }

    return settled;
  } finally {
    stopWatching();
  }
};

/**
 * CallReading is what a body read knows about the call it reads for: the operation a failure
 * names, whether repeating the call is safe, the `Idempotency-Key` it sent, where it sent one,
 * and the caller's signal.
 */
type CallReading = Pick<RequestArgs, "idempotencyKey" | "idempotent" | "operationId" | "signal">;

/** The bounds one read of a body runs under, each absent where the read has none. */
interface DrainBounds {
  /** The most bytes read: a body past it is cut, and none of it is kept. */
  readonly maxBytes?: number | undefined;
  /** The caller's signal, whose abort cancels the body and ends the read. */
  readonly signal?: AbortSignal | undefined;
  /** The longest the read lasts, in milliseconds: a body still arriving then is cut. */
  readonly timeoutMs?: number | undefined;
}

/** What a read of a body came to. */
interface Drained {
  /** The bytes that arrived, in order: all of them for a whole body, none past `maxBytes`. */
  readonly bytes: Uint8Array<ArrayBuffer>;
  /** What the stream failed with, where it broke off before its end. */
  readonly brokeOff?: { readonly cause: unknown } | undefined;
}

/** concat joins a body's chunks into one buffer of exactly their length. */
const concat = (chunks: readonly Uint8Array[], byteLength: number): Uint8Array<ArrayBuffer> => {
  const bytes = new Uint8Array(byteLength);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }

  return bytes;
};

/**
 * drain reads a body to its end through a reader of its own, under `bounds`, and always lets go
 * of the body: a read that ends early, for any reason, cancels it, which releases its connection
 * and its lock.
 *
 * A body that breaks off is reported rather than thrown, with what arrived before it did, so a
 * caller decides what a broken body means for the call it reads for. A body that hands out no
 * reader, because something else holds one on it, is reported the same way with nothing read:
 * what the stream refused with is the runtime's own `TypeError`, outside the family a caller
 * catches.
 *
 * @throws the caller's abort reason, from {@link abortReasonOf}, where `bounds.signal` has
 *   aborted before the read or aborts during it.
 */
const drain = async (body: ReadableStream<Uint8Array>, bounds: DrainBounds): Promise<Drained> => {
  const { maxBytes, signal, timeoutMs } = bounds;

  let reader: ReadableStreamDefaultReader<Uint8Array>;
  try {
    reader = body.getReader();
  } catch (cause) {
    // The caller's abort is theirs whatever state the body is in.
    if (signal !== undefined && isAborted(signal)) {
      throw abortReasonOf(signal);
    }

    return { brokeOff: { cause }, bytes: concat([], 0) };
  }

  // Cancelling settles a read still pending, whatever the fetch that opened the body does with
  // the request's signal, and lets the connection go. A body already errored or closed has
  // nothing to cancel, and its refusal is dropped for that reason.
  const cancel = (reason?: unknown): void => {
    reader.cancel(reason).catch(() => undefined);
  };
  const letGo = (): void => {
    try {
      reader.releaseLock();
    } catch {
      // A runtime that keeps the lock while a read is pending lets it go once the read settles.
    }
  };

  if (signal !== undefined && isAborted(signal)) {
    cancel(abortReasonOf(signal));
    letGo();

    throw abortReasonOf(signal);
  }

  const onAbort = (): void => {
    if (signal !== undefined) {
      cancel(abortReasonOf(signal));
    }
  };
  signal?.addEventListener("abort", onAbort, { once: true });

  const deadline = timeoutMs === undefined ? undefined : setTimeout(cancel, timeoutMs);

  const chunks: Uint8Array[] = [];
  let received = 0;
  let brokeOff: Drained["brokeOff"];
  try {
    while (true) {
      const { done, value } = await reader.read();

      // A cancelled read settles as a body that ended, so the abort is read off the signal.
      if (signal !== undefined && isAborted(signal)) {
        throw abortReasonOf(signal);
      }

      // The deadline's cancel ends the read as a body that ended too: what arrived by then is
      // what the read answers.
      if (done) {
        break;
      }

      received += value.byteLength;
      if (maxBytes !== undefined && received > maxBytes) {
        // Not what the caller reads for, so none of it is kept.
        chunks.length = 0;
        received = 0;
        break;
      }

      chunks.push(value);
    }
  } catch (cause) {
    if (signal !== undefined && isAborted(signal)) {
      throw abortReasonOf(signal);
    }

    brokeOff = { cause };
  } finally {
    clearTimeout(deadline);
    signal?.removeEventListener("abort", onAbort);
    cancel();
    letGo();
  }

  return { brokeOff, bytes: concat(chunks, received) };
};

/**
 * The next step a body that broke off names, by whether repeating the call is safe: it is where
 * the operation declares itself idempotent, and where the call sent a key, which the API answers
 * with the answer it kept.
 */
const brokenBodyStep = (call: CallReading): string => {
  if (call.idempotent) {
    return "Retry the call, or check the network path to the API.";
  }

  return call.idempotencyKey === undefined
    ? "The call was answered, so check whether it took effect before repeating it, and check the network path to the API."
    : "The call was answered, so send it again with this error's idempotencyKey, never a new one, and the API answers with the answer it kept; check the network path to the API.";
};

/** brokenBody builds the failure a success's body that broke off is reported as. */
const brokenBody = (call: CallReading, cause: unknown): MountayaConnectionError =>
  new MountayaConnectionError(
    `${call.operationId}: the response arrived and its body broke off before it was complete. ${brokenBodyStep(call)}`,
    cause,
    call.idempotencyKey,
  );

/**
 * readBuffered reads a success's body through the runtime's own reader (`text`, `arrayBuffer`),
 * for a response whose body the runtime exposes no stream of, and reports a failed read as
 * {@link readText} does.
 */
const readBuffered = async <T>(read: () => Promise<T>, call: CallReading): Promise<T> => {
  try {
    return await settleOnAbort(read, call.signal);
  } catch (cause) {
    if (call.signal !== undefined && isAborted(call.signal)) {
      throw abortReasonOf(call.signal);
    }

    throw brokenBody(call, cause);
  }
};

/**
 * streamOf reads a response's body as the stream to drain, or undefined where there is none to:
 * a response with no body, a runtime whose fetch buffers every body and exposes no stream, a
 * body that is no web stream, or a body something read before this client did. Read against a
 * widened type, since `lib.dom` declares a body on every `Response`.
 *
 * A `fetch` wrapped around the runtime's own that reads a response without cloning it hands back
 * a body already read. Its stream is at its end, so drained it would read as an empty body, an
 * answer the API did not give. Left to the runtime's own reader instead, the read is refused and
 * the refusal is reported as a body that broke off is.
 *
 * A `fetch` option built on another stream family answers a body that hands out no reader, a
 * Node `Readable` for one. Its response reads that body through `text` and `arrayBuffer`, so the
 * read is left to them, as it is for a body the runtime buffers.
 */
const streamOf = (response: Response): ReadableStream<Uint8Array> | undefined => {
  const body: ReadableStream<Uint8Array> | null | undefined = response.body;
  if (response.bodyUsed || body === null || body === undefined) {
    return undefined;
  }

  return typeof body.getReader === "function" ? body : undefined;
};

/**
 * readBytes reads a success's body whole, as bytes, and reports a body that breaks off before
 * it is complete as the connection failure it is.
 *
 * The response has arrived by then, so what can go wrong is the connection: a socket dropped
 * mid-body, a proxy that cut it off. The runtime reports that as a `TypeError`, outside the
 * family a caller catches, and a caller that classifies failures would file a dropped download
 * as a bug in their own program. It becomes a `MountayaConnectionError`, carrying the runtime's
 * error as its `cause`, and it is not retried: a response did arrive, and for a write its effect
 * already happened. The message's next step says as much: a call that is safe to repeat is
 * retried, and a write is checked for its effect before it is repeated.
 *
 * A caller's abort is theirs: it cancels the body, which lets go of the body's lock and of its
 * connection on every runtime, and settles the read at once with the signal's own reason, never
 * rewrapped. A body aborted before its read began is cancelled the same way. Where the runtime
 * exposes no stream of the body, the read goes through the runtime's own and an abort abandons
 * it, see {@link settleOnAbort}.
 *
 * @throws `MountayaConnectionError` for a body that broke off; the caller's abort reason for a
 *   read the caller aborted.
 */
export const readBytes = async (response: Response, call: CallReading): Promise<ArrayBuffer> => {
  const body = streamOf(response);
  if (body === undefined) {
    return readBuffered(() => response.arrayBuffer(), call);
  }

  const drained = await drain(body, { signal: call.signal });
  if (drained.brokeOff !== undefined) {
    throw brokenBody(call, drained.brokeOff.cause);
  }

  return drained.bytes.buffer;
};

/**
 * readText reads a success's body whole, as the UTF-8 text it is, under the rules of
 * {@link readBytes}.
 *
 * @throws as {@link readBytes} does.
 */
export const readText = async (response: Response, call: CallReading): Promise<string> =>
  streamOf(response) === undefined
    ? readBuffered(() => response.text(), call)
    : new TextDecoder().decode(await readBytes(response, call));

/**
 * readFailureText reads what a refusal's body says, as text, from at most
 * {@link MAX_REFUSAL_BYTES} of it within {@link REFUSAL_WAIT_MS}, and never fails on the body:
 * one past the size bound reads as `""`, and one that broke off or did not arrive whole reads as
 * far as it arrived, which is then no envelope.
 *
 * The bounds keep a refusal a failure the caller hears of. The body only sharpens an error the
 * status already classifies, so past either bound it is let go and the status decides alone.
 *
 * @throws the caller's abort reason for a read the caller aborted.
 */
export const readFailureText = async (
  response: Response,
  signal: AbortSignal | undefined,
): Promise<string> => {
  const body = streamOf(response);
  if (body === undefined) {
    return settleOnAbort(() => response.text().catch(() => ""), signal);
  }

  const { bytes } = await drain(body, {
    maxBytes: MAX_REFUSAL_BYTES,
    signal,
    timeoutMs: REFUSAL_WAIT_MS,
  });

  return new TextDecoder().decode(bytes);
};

/**
 * readFailure builds the error a non-2xx response maps to, reading the response's body under the
 * bounds of {@link readFailureText} and under the caller's signal.
 *
 * The body only improves the message of an error the status already classifies, so a body that
 * does not arrive, arrives past a bound or does not parse is tolerated: the status alone then
 * decides the class, and a `402` naming no code is a `MountayaBillingError`. A caller's abort
 * still ends the read: the caller who left is not waiting for a better message.
 *
 * What the call's operation declares, where the caller states it, sharpens the step the message
 * names: a request descriptor states it as it is, so a call through the transport hands its
 * `RequestArgs` over whole, with the `idempotencyKey` the call sent, which the error carries.
 *
 * @throws the caller's abort reason for a read the caller aborted.
 */
export const readFailure = async (
  response: Response,
  call: Pick<CallReading, "idempotencyKey" | "operationId" | "signal"> & FailureDeclaration,
): Promise<MountayaApiError> => {
  const declaration: FailureDeclaration = {
    actsAsMember: call.actsAsMember,
    requiresSecretKey: call.requiresSecretKey,
  };

  if (streamOf(response) === undefined) {
    return settleOnAbort(
      () => errorFromResponse(response, call.operationId, declaration, call.idempotencyKey),
      call.signal,
    );
  }

  return errorFromBody(
    await readFailureText(response, call.signal),
    response,
    call.operationId,
    declaration,
    call.idempotencyKey,
  );
};

/**
 * parseSuccessBody parses the body of a success a call answers with JSON, decoding no `Date` (the
 * caller decodes the instants its mask names), and reports a body that is not JSON as the
 * connection failure it is.
 *
 * The API answers JSON or nothing, so a success that does not parse came from something standing
 * in its place: a captive portal's sign-in page, a proxy's own page, or a base URL that names a
 * web application rather than the API. The runtime's `SyntaxError` is outside the family a caller
 * catches, and the body is no answer the API gave, so it becomes a `MountayaConnectionError`
 * naming the call, the status and the base URL to check, carrying the `SyntaxError` as its
 * `cause`. It is not retried: an answer arrived, and for a write its effect is unknown. An empty
 * body is no body, which every delete answers with, and parses as undefined.
 *
 * One rule for every call that answers JSON: a REST or Tile API call through
 * `MountayaTransport`'s `request`, a Data API call and a collaboration signal alike.
 *
 * @throws `MountayaConnectionError` for a body that is not JSON, carrying the `idempotencyKey`
 *   the call sent, where it sent one.
 */
export const parseSuccessBody = (
  text: string,
  status: number,
  call: Pick<RequestArgs, "idempotencyKey" | "operationId" | "surface">,
): unknown => {
  try {
    return parseJsonWithDates(text);
  } catch (cause) {
    throw new MountayaConnectionError(
      `${call.operationId}: the ${status} answer is not JSON, so something on the network path answered in the API's place, such as a captive portal or a proxy. Check the network path to the API, and that baseUrls.${call.surface} names the API.`,
      cause,
      call.idempotencyKey,
    );
  }
};
